import { Activity } from 'lucide-react';
import type { ServerListing } from '../../utils/statusMonitorTable';
import type { LiveLookup } from '../../hooks/useFleetStatus';
import { cn } from '../../utils/cn';
import { formatReleaseAt } from '../../utils/comingSoon';

/**
 * A server's liveness, in the same terms the status page uses.
 *
 * Maintenance outranks online/offline in what is said, because a server that
 * is down on purpose is not an incident and must not read like one. A server
 * the public list has never heard of is called unlisted rather than offline:
 * that is usually a pack that is hidden, brand new, or misspelt.
 *
 * Coming soon outranks maintenance: it is the same window underneath, but it
 * is a new server waiting to open, and saying "in maintenance" would suggest
 * something broke.
 */

export type LiveState = 'online' | 'offline' | 'maintenance' | 'coming_soon' | 'unlisted' | 'unknown';

export interface LiveDescription {
  state: LiveState;
  label: string;
  /** A second line where there is something to add: player count, since when. */
  detail: string | null;
  tone: 'success' | 'danger' | 'warning' | 'soon' | 'neutral';
}

const since = (at: number | null) =>
  at ? `since ${new Date(at).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}` : null;

export function describeLive({ listing, maintenance, comingSoon, releaseAt, loaded }: LiveLookup): LiveDescription {
  if (!loaded && !listing) return { state: 'unknown', label: 'Checking…', detail: null, tone: 'neutral' };
  if (comingSoon) {
    return {
      state: 'coming_soon',
      label: 'Coming soon',
      detail: releaseAt ? `opens ${formatReleaseAt(releaseAt)}` : 'not open to the public yet',
      tone: 'soon',
    };
  }
  if (maintenance) {
    return {
      state: 'maintenance',
      label: 'In maintenance',
      detail: listing ? (listing.online ? `${listing.players} / ${listing.maxPlayers} still on` : 'Offline') : null,
      tone: 'warning',
    };
  }
  if (!listing) return { state: 'unlisted', label: 'Not in server list', detail: null, tone: 'neutral' };
  if (listing.online) {
    return {
      state: 'online',
      label: 'Online',
      detail: `${listing.players} / ${listing.maxPlayers} players`,
      tone: 'success',
    };
  }
  return { state: 'offline', label: 'Offline', detail: since(listing.lastOfflineAt), tone: 'danger' };
}

const DOT_TONE: Record<LiveDescription['tone'], string> = {
  success: 'bg-emerald-400',
  danger: 'bg-red-400',
  warning: 'bg-amber-400',
  soon: 'bg-gradient-to-br from-violet-400 to-pink-400',
  neutral: 'bg-glass-500',
};

export const LiveDot = ({ description, className }: { description: LiveDescription; className?: string }) => (
  <span
    className={cn(
      'inline-block w-2 h-2 rounded-full shrink-0',
      DOT_TONE[description.tone],
      description.state === 'unknown' && 'animate-pulse',
      className,
    )}
    aria-hidden
  />
);

/** Dot + word, for table cells and headers. */
export const LiveStatusBadge = ({ live, className, withDetail = false }: {
  live: LiveLookup;
  className?: string;
  withDetail?: boolean;
}) => {
  const description = describeLive(live);
  return (
    <span
      className={cn('inline-flex items-center gap-1.5 text-xs whitespace-nowrap', className)}
      title={[description.label, description.detail].filter(Boolean).join(' · ')}
    >
      <LiveDot description={description} />
      <span className={cn(
        description.tone === 'success' && 'text-emerald-300',
        description.tone === 'danger' && 'text-red-300',
        description.tone === 'warning' && 'text-amber-300',
        description.tone === 'soon' && 'text-violet-300',
        description.tone === 'neutral' && 'text-glass-400',
      )}>
        {description.label}
      </span>
      {withDetail && description.detail && (
        <span className="text-glass-400 tabular-nums">· {description.detail}</span>
      )}
    </span>
  );
};

/** The sum over a set of servers, for a cluster heading. */
export function summariseLive(lookups: LiveLookup[]): { online: number; offline: number; maintenance: number; comingSoon: number; unlisted: number; loaded: boolean } {
  const summary = { online: 0, offline: 0, maintenance: 0, comingSoon: 0, unlisted: 0, loaded: lookups.every((l) => l.loaded) };
  for (const lookup of lookups) {
    const { state } = describeLive(lookup);
    if (state === 'online') summary.online += 1;
    else if (state === 'offline') summary.offline += 1;
    else if (state === 'maintenance') summary.maintenance += 1;
    else if (state === 'coming_soon') summary.comingSoon += 1;
    else if (state === 'unlisted') summary.unlisted += 1;
  }
  return summary;
}

export const ClusterLiveSummary = ({ lookups, className }: { lookups: LiveLookup[]; className?: string }) => {
  const s = summariseLive(lookups);
  if (!s.loaded && lookups.every((l) => !l.listing)) {
    return <span className={cn('text-xs text-glass-500 inline-flex items-center gap-1', className)}><Activity className="w-3 h-3 animate-pulse" />Checking status…</span>;
  }
  const parts: { text: string; tone: string }[] = [];
  if (s.online) parts.push({ text: `${s.online} online`, tone: 'text-emerald-300' });
  if (s.offline) parts.push({ text: `${s.offline} offline`, tone: 'text-red-300' });
  if (s.maintenance) parts.push({ text: `${s.maintenance} in maintenance`, tone: 'text-amber-300' });
  if (s.comingSoon) parts.push({ text: `${s.comingSoon} coming soon`, tone: 'text-violet-300' });
  if (s.unlisted) parts.push({ text: `${s.unlisted} not listed`, tone: 'text-glass-400' });
  return (
    <span className={cn('inline-flex items-center gap-2 text-xs tabular-nums', className)}>
      {parts.map((p) => <span key={p.text} className={p.tone}>{p.text}</span>)}
    </span>
  );
};

export type { ServerListing };
